import { z } from 'zod'

/**
 * Every editable leaf is stored as an envelope so prefill/confirm/author state travels with the
 * value and is written atomically with a dotted-path update. This file is the only place the
 * flags change. Web, functions and the import CLI all go through applyEdit / setConfirmed.
 */
export const FieldSource = z.enum(['import', 'staff', 'client'])
export type FieldSource = z.infer<typeof FieldSource>

export type Actor = { kind: 'staff' | 'client' | 'import'; id: string }

export type Field<T = string> = {
  value: T
  /** Sonder supplied this value (staff or import). */
  prefilled: boolean
  /** Client pressed "Looks right", or edited the value themselves. */
  confirmed: boolean
  source: FieldSource
  /** uid, or "import:<runId>". */
  updatedBy: string
  /** ms since epoch; kept as a number so this package has no Firestore dependency. */
  updatedAt: number
  /** Who pressed "Looks right" (or edited it, which also confirms), and when. Absent on older data. */
  confirmedBy?: string
  confirmedAt?: number
  /**
   * The field as it stood before the client's first edit of a prefilled value (Sonder's text, and whether it was
   * already confirmed), so Undo, or typing it back exactly, returns it to that state. Older entries lack the flags.
   */
  original?: Original<T>
}

type Original<T> = { value: T; source: FieldSource; updatedBy: string; updatedAt: number; prefilled?: boolean; confirmed?: boolean; confirmedBy?: string; confirmedAt?: number }

export function fieldOf<T extends z.ZodTypeAny>(value: T) {
  return z.object({
    value,
    prefilled: z.boolean(),
    confirmed: z.boolean(),
    source: FieldSource,
    updatedBy: z.string(),
    updatedAt: z.number(),
    confirmedBy: z.string().optional(),
    confirmedAt: z.number().optional(),
    original: z
      .object({ value, source: FieldSource, updatedBy: z.string(), updatedAt: z.number(), prefilled: z.boolean().optional(), confirmed: z.boolean().optional(), confirmedBy: z.string().optional(), confirmedAt: z.number().optional() })
      .optional(),
  })
}
export const StringField = fieldOf(z.string())

export function isEmpty(value: unknown): boolean {
  if (value == null) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value as object).length === 0
  return false
}

export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (typeof a !== typeof b || a == null || b == null) return false
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false
    return a.every((x, i) => deepEqual(x, b[i]))
  }
  if (typeof a === 'object') {
    const ka = Object.keys(a as object)
    const kb = Object.keys(b as object)
    if (ka.length !== kb.length) return false
    return ka.every((k) => deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]))
  }
  return false
}

export function emptyField<T>(value: T, now = Date.now()): Field<T> {
  return { value, prefilled: false, confirmed: false, source: 'staff', updatedBy: 'system', updatedAt: now }
}

/**
 * Compute the next envelope for a value change. Returns null when nothing should be written
 * (value unchanged), which keeps re-imports idempotent and never un-confirms silently.
 */
export function applyEdit<T>(existing: Field<T> | undefined, value: T, actor: Actor, now = Date.now()): Field<T> | null {
  if (existing && deepEqual(existing.value, value)) return null
  const updatedBy = actor.kind === 'import' ? `import:${actor.id}` : actor.id
  if (actor.kind === 'client') {
    // Keep Sonder's version from the first edit; later edits keep the same one.
    const original = existing?.original ?? (existing?.prefilled && existing.source !== 'client' ? snapshot(existing) : undefined)
    // Typed back to exactly what Sonder wrote (a character deleted and retyped, say): not an edit after all.
    if (original && deepEqual(original.value, value)) return restore(original)
    const next: Field<T> = { value, prefilled: existing?.prefilled ?? false, confirmed: true, source: 'client', updatedBy, updatedAt: now, confirmedBy: updatedBy, confirmedAt: now }
    if (original) next.original = original
    return next
  }
  // A new value from Sonder starts over: nothing confirmed, nothing to undo back to.
  return { value, prefilled: true, confirmed: false, source: actor.kind, updatedBy, updatedAt: now }
}

/** The client changed a value Sonder supplied. */
export function isEdited(f: Field<unknown> | undefined): boolean {
  return Boolean(f && f.prefilled && f.source === 'client')
}

/** "Undo changes": the field exactly as it was before the client edited it. Null when there is nothing to go back to. */
export function undoEdit<T>(existing: Field<T> | undefined): Field<T> | null {
  return existing?.original ? restore(existing.original) : null
}

/** The field before an edit, without undefined keys (Firestore rejects them). */
function snapshot<T>(f: Field<T>): Original<T> {
  const o: Original<T> = { value: f.value, source: f.source, updatedBy: f.updatedBy, updatedAt: f.updatedAt, prefilled: f.prefilled, confirmed: f.confirmed }
  if (f.confirmedBy !== undefined) o.confirmedBy = f.confirmedBy
  if (f.confirmedAt !== undefined) o.confirmedAt = f.confirmedAt
  return o
}

function restore<T>(o: Original<T>): Field<T> {
  const f: Field<T> = { value: o.value, prefilled: o.prefilled ?? true, confirmed: o.confirmed ?? false, source: o.source, updatedBy: o.updatedBy, updatedAt: o.updatedAt }
  if (f.confirmed && o.confirmedBy !== undefined) f.confirmedBy = o.confirmedBy
  if (f.confirmed && o.confirmedAt !== undefined) f.confirmedAt = o.confirmedAt
  return f
}

/** Client "Looks right" toggle. Staff may also clear a confirmation. */
export function setConfirmed<T>(existing: Field<T>, confirmed: boolean, actor: Actor, now = Date.now()): Field<T> | null {
  if (existing.confirmed === confirmed) return null
  const by = actor.kind === 'import' ? `import:${actor.id}` : actor.id
  // Keys are dropped rather than set to undefined, which Firestore rejects.
  const { confirmedBy: _by, confirmedAt: _at, ...rest } = existing
  return confirmed ? { ...rest, confirmed, updatedBy: by, updatedAt: now, confirmedBy: by, confirmedAt: now } : { ...rest, confirmed, updatedBy: by, updatedAt: now }
}

/** A field counts toward progress when it has a value and, if we supplied it, the client agreed. */
export function isComplete(f: Field<unknown> | undefined): boolean {
  if (!f) return false
  return !isEmpty(f.value) && (f.confirmed || !f.prefilled)
}

/** Needs the client's eyes: we supplied it and they have not confirmed. */
export function needsReview(f: Field<unknown> | undefined): boolean {
  return Boolean(f && f.prefilled && !f.confirmed && !isEmpty(f.value))
}
